import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { X, CheckCircle, AlertCircle, Loader, Play } from 'lucide-react';
import api from '../services/api';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg);
  position: relative;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--primary-700);
  color: white;
  border-radius: 12px 12px 0 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Content = styled.div`
  padding: 2rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 12px;
  background: #e9ecef;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProgressFill = styled.div<{ progress: number; status: 'running' | 'completed' | 'error' }>`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => {
        if (props.status === 'error') return 'var(--error)';
        if (props.status === 'completed') return 'var(--success)';
        return 'var(--primary-600)';
    }};
  transition: width 0.5s ease-in-out;
  position: relative;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StepCard = styled.div<{ status: 'pending' | 'running' | 'completed' | 'error' | 'waiting' }>`
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid ${props => {
        switch (props.status) {
            case 'running': return 'var(--primary-600)';
            case 'completed': return 'var(--success)';
            case 'error': return 'var(--error)';
            case 'waiting': return 'var(--accent-500)';
            default: return '#e9ecef';
        }
    }};
  background: ${props => {
        switch (props.status) {
            case 'running': return 'var(--primary-50)';
            case 'completed': return 'var(--success-soft)';
            case 'error': return 'var(--error-soft)';
            default: return 'white';
        }
    }};
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.5rem;
`;

const StepNumber = styled.div<{ status: 'pending' | 'running' | 'completed' | 'error' | 'waiting' }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  flex-shrink: 0;
  background: ${props => {
        switch (props.status) {
            case 'running': return 'var(--primary-600)';
            case 'completed': return 'var(--success)';
            case 'error': return 'var(--error)';
            case 'waiting': return 'var(--accent-500)';
            default: return '#f8f9fa';
        }
    }};
  color: ${props => props.status === 'pending' ? '#6c757d' : 'white'};
`;

const StepTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  flex: 1;
`;

const StepDescription = styled.div`
  color: #6c757d;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-top: 0.5rem;
  padding-left: 56px;
`;

const ReasoningBox = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--primary-50);
  border-left: 4px solid var(--primary-500);
  border-radius: 8px;
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.6;
  color: #495057;
  max-height: 300px;
  overflow-y: auto;
`;

const ContinueButton = styled.button`
  background: var(--accent-600);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  font-size: 1rem;
  box-shadow: var(--shadow-sm);
  transition: background 0.2s ease;

  &:hover {
    background: var(--accent-700);
    box-shadow: var(--shadow-md);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PromptButton = styled.button`
  background: var(--primary-700);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary-800);
  }
`;

const PromptBox = styled.div`
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  color: #495057;
  max-height: 300px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const DataBox = styled.div`
  background: var(--primary-50);
  border-left: 4px solid var(--primary-500);
  padding: 1rem;
  margin-top: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;

  strong {
    color: var(--primary-700);
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const StatusBadge = styled.div<{ status: 'running' | 'completed' | 'error' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  background: ${props => {
        switch (props.status) {
            case 'running': return 'var(--primary-600)';
            case 'completed': return 'var(--success)';
            case 'error': return 'var(--error)';
            default: return '#e9ecef';
        }
    }};
  color: white;
  margin-top: 1rem;
`;

interface ForecastProgressModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete?: (previsioneId: number) => void;
}

interface StepProgress {
    step: number;
    title: string;
    description: string;
    status: 'pending' | 'running' | 'completed' | 'error' | 'waiting';
    reasoning?: string;
    timestamp?: string;
    sessionId?: string;
    prompt?: string;
    data?: any;
}

const STEP_TITLES = [
    'Inizializzazione',
    'Estrazione Storico',
    'Aggregazione Dati',
    'Calcolo Crescita',
    'Analisi Mix Gusti',
    'Previsione 2026',
    'Calcolo Ingredienti',
    'Aggregazione Fornitori',
    'Salvataggio'
];

export const ForecastProgressModal: React.FC<ForecastProgressModalProps> = ({ 
    isOpen, 
    onClose,
    onComplete 
}) => {
    const [steps, setSteps] = useState<StepProgress[]>([]);
    const [currentStep, setCurrentStep] = useState(0);
    const [overallStatus, setOverallStatus] = useState<'running' | 'completed' | 'error'>('running');
    const [, setExpandedSteps] = useState<Set<number>>(new Set());
    const [, setPrevisioneId] = useState<number | null>(null);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [waitingForContinue, setWaitingForContinue] = useState<number | null>(null);
    const [showPrompt, setShowPrompt] = useState<number | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);

    const handleContinue = async () => {
        if (waitingForContinue === null || !sessionId) return;

        try {
            console.log(`▶️ Continua step ${waitingForContinue}...`);
            
            await api.post('/forecasts/annual/continue', {
                session_id: sessionId,
                step: waitingForContinue
            });

            setWaitingForContinue(null);
        } catch (error) {
            console.error('Errore continue step:', error);
        }
    };

    useEffect(() => {
        if (!isOpen) return;

        // Inizializza step
        const initialSteps: StepProgress[] = STEP_TITLES.map((title, index) => ({
            step: index,
            title,
            description: 'In attesa...',
            status: 'pending'
        }));
        setSteps(initialSteps);

        // Connetti a SSE endpoint
        const token = localStorage.getItem('gelato_token_dev') || localStorage.getItem('gelato_token_prod');
        
        // Usa /api per il proxy, SSE funziona meglio con path relativo
        const sseUrl = `/api/forecasts/annual/stream?token=${encodeURIComponent(token || '')}`;
        
        console.log('🔌 Connessione SSE a:', sseUrl);
        const eventSource = new EventSource(sseUrl);

        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                
                // Salva sessionId
                if (data.sessionId && !sessionId) {
                    setSessionId(data.sessionId);
                }
                
                console.log('📨 SSE message:', {
                    step: data.step,
                    status: data.status,
                    title: data.title,
                    hasReasoning: !!data.reasoning,
                    reasoningLength: data.reasoning?.length || 0,
                    reasoningPreview: data.reasoning?.substring(0, 100),
                    description: data.description?.substring(0, 100)
                });

                setSteps(prev => {
                    const newSteps = [...prev];
                    const stepIndex = data.step;
                    
                    if (stepIndex >= 0 && stepIndex < newSteps.length) {
                        // Preserva reasoning, prompt e data dello step precedente se non presenti nel nuovo messaggio
                        const existingStep = newSteps[stepIndex];
                        
                        newSteps[stepIndex] = {
                            step: stepIndex,
                            title: data.title || STEP_TITLES[stepIndex],
                            description: data.description || existingStep.description || '',
                            status: data.status,
                            reasoning: data.reasoning || existingStep.reasoning,
                            timestamp: data.timestamp || existingStep.timestamp,
                            sessionId: data.sessionId || existingStep.sessionId,
                            prompt: data.prompt || existingStep.prompt,
                            data: data.data || existingStep.data
                        };
                        
                        // Auto-espandi il reasoning quando arriva
                        if (data.reasoning && data.status === 'completed') {
                            setExpandedSteps(prev => {
                                const newSet = new Set(prev);
                                newSet.add(stepIndex);
                                return newSet;
                            });
                        }
                    }
                    
                    return newSteps;
                });

                setCurrentStep(data.step);
                
                // Gestisci stato waiting
                if (data.status === 'waiting') {
                    setWaitingForContinue(data.step);
                }

                if (data.status === 'completed' && data.step === 8) {
                    setOverallStatus('completed');
                    
                    // Estrai ID previsione dalla descrizione
                    const match = data.description.match(/ID (\d+)/);
                    if (match) {
                        const id = parseInt(match[1]);
                        setPrevisioneId(id);
                        if (onComplete) {
                            setTimeout(() => onComplete(id), 2000);
                        }
                    }
                } else if (data.status === 'error') {
                    setOverallStatus('error');
                }
            } catch (error) {
                console.error('Errore parsing SSE message:', error);
            }
        };

        eventSource.onerror = (error) => {
            console.error('SSE error:', error);
            setOverallStatus('error');
            eventSource.close();
        };

        return () => {
            console.log('🔌 Chiusura connessione SSE');
            eventSource.close();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- connessione SSE aperta solo al toggle di isOpen; onComplete/sessionId causerebbero riconnessioni
    }, [isOpen]); // Rimuovo onComplete dalle dipendenze per evitare riconnessioni

    const progress = ((currentStep + 1) / STEP_TITLES.length) * 100;

    if (!isOpen) return null;

    return (
        <Overlay>
            <Modal>
                <Header>
                    <Title>Generazione Previsione AI</Title>
                    <CloseButton onClick={onClose} disabled={overallStatus === 'running'}>
                        <X size={20} />
                    </CloseButton>
                </Header>

                <Content>
                    <ProgressBar>
                        <ProgressFill progress={progress} status={overallStatus} />
                    </ProgressBar>

                    <StepList>
                        {steps.map((step, index) => (
                            <StepCard key={index} status={step.status}>
                                <StepHeader>
                                    <StepNumber status={step.status}>
                                        {step.status === 'completed' ? (
                                            <CheckCircle size={24} />
                                        ) : step.status === 'error' ? (
                                            <AlertCircle size={24} />
                                        ) : step.status === 'running' ? (
                                            <Loader size={24} className="spinner" />
                                        ) : step.status === 'waiting' ? (
                                            <Play size={24} />
                                        ) : (
                                            index
                                        )}
                                    </StepNumber>
                                    <StepTitle>{step.title}</StepTitle>
                                    {step.status === 'running' && (
                                        <Loader size={20} className="spinner" style={{ color: 'var(--primary-600)' }} />
                                    )}
                                </StepHeader>

                                <StepDescription>{step.description}</StepDescription>

                                {/* Mostra sempre reasoning se disponibile */}
                                {step.reasoning && (
                                    <ReasoningBox>
                                        <strong>Ragionamento AI:</strong>
                                        {step.reasoning}
                                    </ReasoningBox>
                                )}

                                {/* Mostra dati processati se disponibili */}
                                {step.data && (
                                    <DataBox>
                                        <strong>Dati processati:</strong>
                                        {JSON.stringify(step.data, null, 2)}
                                    </DataBox>
                                )}

                                {/* Bottoni azione */}
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                                    {step.prompt && (
                                        <PromptButton onClick={() => setShowPrompt(showPrompt === index ? null : index)}>
                                            {showPrompt === index ? 'Nascondi' : 'Vedi'} Prompt
                                        </PromptButton>
                                    )}

                                    {step.status === 'waiting' && waitingForContinue === index && (
                                        <ContinueButton onClick={handleContinue}>
                                            <Play size={20} />
                                            Continua allo Step {index + 1}
                                        </ContinueButton>
                                    )}
                                </div>

                                {/* Mostra prompt se richiesto */}
                                {showPrompt === index && step.prompt && (
                                    <PromptBox>
                                        <strong>Prompt inviato a OpenAI:</strong><br/><br/>
                                        {step.prompt}
                                    </PromptBox>
                                )}
                            </StepCard>
                        ))}
                    </StepList>

                    {overallStatus === 'completed' && (
                        <StatusBadge status="completed">
                            <CheckCircle size={20} />
                            Previsione completata
                        </StatusBadge>
                    )}

                    {overallStatus === 'error' && (
                        <StatusBadge status="error">
                            <AlertCircle size={20} />
                            Errore durante la generazione
                        </StatusBadge>
                    )}
                </Content>
            </Modal>

            <style>{`
                .spinner {
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Overlay>
    );
};

export default ForecastProgressModal;
